/**
 * Release notes: the product announcements shown to users of the Adnuntius
 * apps. They are not network scoped — one set serves every network — and the
 * backend lets only its own admin accounts list them all, read one by id or
 * save one (`ReleaseNotesResource` in core).
 */

import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { AssetsApi } from '@/services/assets/assetsApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** The accounts `ReleaseNotesResource.ADMIN_USERNAMES` allows to manage release notes. */
const RELEASE_NOTES_ADMINS = ['adnuntius@adnuntius.com', 'broker1@adnuntius.com']

export function isReleaseNotesAdmin(username: string | undefined): boolean {
  return !!username && RELEASE_NOTES_ADMINS.includes(username.toLowerCase())
}

/** Where the note is shown — mirrors `ReleaseNote.App` in core. */
export const RELEASE_NOTE_APPS = ['MARKETPLACE', 'ADSERVER', 'DATA'] as const
export type ReleaseNoteApp = (typeof RELEASE_NOTE_APPS)[number]

/** `ReleaseNote.Tag` in core. */
export const RELEASE_NOTE_TAGS = ['FEATURE', 'BUGFIX', 'INFO', 'UPDATE'] as const
export type ReleaseNoteTag = (typeof RELEASE_NOTE_TAGS)[number]

/** Whether non-admins can see it at all. */
export type ReleaseNoteStatus = 'DRAFT' | 'PUBLISHED'
/** Archived notes are kept but never shown. */
export type ReleaseNoteState = 'ACTIVE' | 'ARCHIVED'

/**
 * The one user group core knows. A note limited to it is internal: only the
 * Adnuntius admin accounts see it. No group means public.
 */
export const INTERNAL_USER_GROUP = 'INTERNAL'

export interface ReleaseNote {
  id: string
  urlSlug: string
  title: string
  subTitle: string
  /** HTML. */
  content: string
  status: ReleaseNoteStatus
  state: ReleaseNoteState
  /** Empty means the note goes out when it is published. */
  publishTime: string
  apps: ReleaseNoteApp[]
  tags: ReleaseNoteTag[]
  userGroups: string[]
  /**
   * Images uploaded for the note to link to, each as UII spells it:
   * `cdnId{}id{}fileName{}mimeType{}fileSizeBytes{}width{}height`.
   */
  assetStrings: string[]
  author: string
  createTime: string
  updateTime: string
  /** The note as the API sent it, so a save keeps fields this UI does not edit. */
  raw: Record<string, unknown>
}

export type ReleaseNoteInput = Pick<
  ReleaseNote,
  | 'title'
  | 'subTitle'
  | 'content'
  | 'status'
  | 'state'
  | 'publishTime'
  | 'apps'
  | 'tags'
  | 'userGroups'
  | 'assetStrings'
>

export interface ReleaseNoteAsset {
  cdnId: string
  id: string
  fileName: string
  mimeType: string
  fileSizeBytes: number
  width: number
  height: number
}

const ASSET_SEPARATOR = '{}'

export function parseReleaseNoteAsset(assetString: string): ReleaseNoteAsset {
  const [cdnId = '', id = '', fileName = '', mimeType = '', size, width, height] =
    assetString.split(ASSET_SEPARATOR)
  return {
    cdnId,
    id,
    fileName,
    mimeType,
    fileSizeBytes: Number(size) || 0,
    width: Number(width) || 0,
    height: Number(height) || 0,
  }
}

export function formatReleaseNoteAsset(asset: ReleaseNoteAsset): string {
  return [
    asset.cdnId,
    asset.id,
    asset.fileName,
    asset.mimeType,
    asset.fileSizeBytes,
    asset.width,
    asset.height,
  ].join(ASSET_SEPARATOR)
}

/**
 * Uploads an image to the CDN for a release note, resolving with the asset in
 * the spelling the note stores it in.
 */
export async function uploadReleaseNoteAsset(file: File): Promise<ReleaseNoteAsset> {
  const asset = await AssetsApi.uploadToCdn(file)
  return {
    cdnId: asset.cdnId,
    id: asset.id,
    fileName: asset.fileName,
    mimeType: asset.mimeType,
    fileSizeBytes: asset.fileSize,
    width: asset.width,
    height: asset.height,
  }
}

/** The `<img>` tag to paste into a note's content for an uploaded asset. */
export function releaseNoteAssetImgTag(asset: ReleaseNoteAsset): string {
  return `<img src='${asset.cdnId}' width='${asset.width}' height='${asset.height}'>`
}

function mapReleaseNote(r: Record<string, unknown>): ReleaseNote {
  return {
    id: (r.id as string) ?? '',
    urlSlug: (r.urlSlug as string) ?? '',
    title: (r.title as string) ?? '',
    subTitle: (r.subTitle as string) ?? '',
    content: (r.content as string) ?? '',
    status: (r.status as ReleaseNoteStatus) ?? 'DRAFT',
    state: (r.state as ReleaseNoteState) ?? 'ACTIVE',
    publishTime: (r.publishTime as string) ?? '',
    apps: (r.apps as ReleaseNoteApp[]) ?? [],
    tags: (r.tags as ReleaseNoteTag[]) ?? [],
    userGroups: (r.userGroups as string[]) ?? [],
    assetStrings: (r.assetStrings as string[]) ?? [],
    author: (r.author as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
    raw: r,
  }
}

export const ReleaseNotesApi = {
  /** Every note in the given state, drafts and internal ones included. Newest first. */
  getAll: async (state: ReleaseNoteState): Promise<ReleaseNote[]> => {
    const params = new URLSearchParams({ context: getContext(), includeAll: 'true', state })
    const res = await apiFetch(`${API_BASE}/releasenotes?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch release notes')
    const data = await res.json()
    return (Array.isArray(data) ? data : []).map(mapReleaseNote)
  },

  /**
   * The notes readers see: published, active and past their publish time,
   * newest first. Internal notes are left out unless the user is one of the
   * accounts they are meant for.
   */
  getPublished: async (): Promise<ReleaseNote[]> => {
    const params = new URLSearchParams({ context: getContext(), state: 'ACTIVE' })
    const res = await apiFetch(`${API_BASE}/releasenotes?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch release notes')
    const data = await res.json()
    return (Array.isArray(data) ? data : []).map(mapReleaseNote)
  },

  getById: async (id: string): Promise<ReleaseNote> => {
    const res = await apiFetch(`${API_BASE}/releasenotes/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch release note')
    return mapReleaseNote(await res.json())
  },

  /**
   * Creates or replaces the note. Pass the saved note as `existing` when
   * editing, so what this UI does not edit — the URL slug, the author — is
   * kept. The server fills in the publish time when a note is published
   * without one.
   */
  save: async (
    id: string,
    input: ReleaseNoteInput,
    existing?: ReleaseNote | null
  ): Promise<ReleaseNote> => {
    const body = {
      contentType: 'HTML',
      ...existing?.raw,
      ...input,
      id,
      publishTime: input.publishTime || null,
    }
    const res = await apiFetch(`${API_BASE}/releasenotes/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save release note')
    return mapReleaseNote(await res.json())
  },
}
